import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Stack,
  Container,
  Tooltip
} from '@mui/material';
import {
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  LocationOn as LocationIcon,
  People as CultureIcon,
  Settings as SystemIcon,
  Groups as FactionIcon,
  AutoFixHigh as MagicIcon,
  MenuBook as LoreIcon,
  Timeline as TimelineIcon,
  Event as EventIcon
} from '@mui/icons-material';
import {
  PDFDownloadLink, Document, Page, Text, View, StyleSheet, pdf
} from '@react-pdf/renderer';

// Create styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 65, // Increased bottom padding for footer
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
    color: '#1976d2',
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 30,
    breakInside: 'avoid',
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 15,
    color: '#1976d2',
    borderBottom: '1pt solid #e0e0e0',
    paddingBottom: 8,
    fontWeight: 'bold',
  },
  item: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f9f9f9',
    borderRadius: 5,
    borderLeft: '4pt solid #1976d2',
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1976d2',
  },
  description: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 8,
    color: '#666',
  },
  field: {
    fontSize: 12,
    marginBottom: 5,
  },
  fieldLabel: {
    fontWeight: 'bold',
  },
  details: {
    fontSize: 12,
    marginTop: 8,
    lineHeight: 1.5,
  },
  tag: {
    fontSize: 10,
    backgroundColor: '#e0e0e0',
    padding: '2px 6px',
    borderRadius: 4,
    marginRight: 5,
    display: 'inline-block',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 10,
    color: 'grey',
    borderTop: '1pt solid #e0e0e0',
    paddingTop: 10,
  },
  importanceHigh: {
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  importanceMedium: {
    color: '#1976d2',
  },
  importanceLow: {
    color: '#757575',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#757575',
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 5,
  },
  logo: {
    width: 50,
    height: 50,
  },
});

// PDF Document component
const WorldBuildingPDF = ({ worldElements, projectId }) => (
  <Document title={`World Building Report - ${projectId}`}>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>World Building Report</Text>
        <Text style={{ fontSize: 14, textAlign: 'center', color: '#666' }}>Project: {projectId}</Text>
      </View>
      
      {worldElements.locations && worldElements.locations.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Locations ({worldElements.locations.length})</Text>
          {worldElements.locations.map(location => (
            <View key={location.id} style={styles.item}>
              <Text style={styles.itemTitle}>{location.name}</Text>
              {location.description && <Text style={styles.description}>{location.description}</Text>}
              {location.geography && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Geography: </Text>
                  {location.geography}
                </Text>
              )}
              {location.climate && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Climate: </Text>
                  {location.climate}
                </Text>
              )}
              {location.notableFeatures && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Notable Features: </Text>
                  {location.notableFeatures}
                </Text>
              )}
              {location.inhabitants && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Inhabitants: </Text>
                  {location.inhabitants}
                </Text>
              )}
              {location.details && <Text style={styles.details}>{location.details}</Text>}
            </View>
          ))}
        </View>
      )}
      
      {worldElements.cultures && worldElements.cultures.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cultures ({worldElements.cultures.length})</Text>
          {worldElements.cultures.map(culture => (
            <View key={culture.id} style={styles.item}>
              <Text style={styles.itemTitle}>{culture.name}</Text>
              {culture.description && <Text style={styles.description}>{culture.description}</Text>}
              {culture.traditions && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Traditions: </Text>
                  {culture.traditions}
                </Text>
              )}
              {culture.values && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Values: </Text>
                  {culture.values}
                </Text>
              )}
              {culture.socialStructure && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Social Structure: </Text>
                  {culture.socialStructure}
                </Text>
              )}
              {culture.languages && (
                <Text style={styles.field}>
                  <Text style={styles.fieldLabel}>Languages: </Text>
                  {culture.languages}
                </Text>
              )}
              {culture.details && <Text style={styles.details}>{culture.details}</Text>}
            </View>
          ))}
        </View>
      )}
      
      <Text style={styles.footer}>
        Generated on {new Date().toLocaleDateString()} for project {projectId}
      </Text>
      <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
        `${pageNumber} / ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

const WorldBuildingReport = ({ worldElements, projectId, onClose }) => {
  const printRef = useRef();
  
  const handlePrint = () => {
    // Create an invisible iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    // Write the report content to the iframe
    iframe.contentDocument.write(`
      <html>
        <head>
          <title>World Building Report - ${projectId}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');
            
            body {
              font-family: 'Roboto', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 1100px;
              margin: 0 auto;
              padding: 30px;
              background-color: #fff;
            }
            
            .report-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #1976d2;
              padding-bottom: 20px;
            }
            
            .report-title {
              font-size: 28px;
              color: #1976d2;
              margin-bottom: 10px;
              font-weight: 700;
            }
            
            .report-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 5px;
            }
            
            h1 {
              text-align: center;
              color: #1976d2;
              padding-bottom: 10px;
              margin-bottom: 30px;
              font-weight: 500;
            }
            
            h2 {
              color: #1976d2;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 8px;
              margin-top: 40px;
              margin-bottom: 20px;
              font-weight: 500;
              display: flex;
              align-items: center;
            }
            
            h2::before {
              content: "";
              display: inline-block;
              width: 18px;
              height: 18px;
              background-color: #1976d2;
              margin-right: 10px;
              border-radius: 50%;
            }
            
            h3 {
              color: #333;
              margin-top: 25px;
              font-weight: 500;
            }
            
            .item {
              margin-bottom: 25px;
              padding: 20px;
              background-color: #f9f9f9;
              border-radius: 8px;
              border-left: 5px solid #1976d2;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            
            .item h4 {
              margin-top: 0;
              color: #1976d2;
              font-size: 18px;
              font-weight: 500;
              margin-bottom: 12px;
              border-bottom: 1px dashed #e0e0e0;
              padding-bottom: 8px;
            }
            
            .item p {
              margin: 8px 0;
            }
            
            .description {
              font-style: italic;
              color: #666;
              margin-bottom: 15px;
              line-height: 1.5;
            }
            
            .details {
              margin-top: 15px;
              line-height: 1.6;
              background-color: #f5f5f5;
              padding: 10px;
              border-radius: 4px;
            }
            
            .tag {
              display: inline-block;
              background-color: #e0e0e0;
              padding: 3px 10px;
              border-radius: 15px;
              margin-right: 8px;
              margin-bottom: 8px;
              font-size: 0.8em;
              color: #555;
            }
            
            .importance-high {
              color: #d32f2f;
              font-weight: bold;
            }
            
            .importance-medium {
              color: #1976d2;
            }
            
            .importance-low {
              color: #757575;
            }
            
            .timeline-event {
              margin-bottom: 20px;
              padding-bottom: 20px;
              border-bottom: 1px dashed #e0e0e0;
              position: relative;
            }
            
            .timeline-event:last-child {
              border-bottom: none;
            }
            
            .timeline-date {
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 5px;
            }
            
            .timeline-era {
              font-style: italic;
              color: #757575;
              margin-bottom: 10px;
            }
            
            .report-footer {
              margin-top: 50px;
              text-align: center;
              color: #757575;
              font-size: 0.9em;
              border-top: 1px solid #e0e0e0;
              padding-top: 20px;
            }
            
            .field-label {
              font-weight: 500;
              color: #555;
            }
            
            .tags-container {
              margin-top: 12px;
            }
            
            @media print {
              body {
                padding: 0;
                margin: 15mm;
              }
              
              button {
                display: none;
              }
              
              .item {
                break-inside: avoid;
              }
              
              .report-header {
                position: running(header);
              }
              
              .report-footer {
                position: running(footer);
              }
              
              @page {
                margin: 15mm;
                @top-center {
                  content: element(header);
                }
                @bottom-center {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 10pt;
                  color: #757575;
                }
              }
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div class="report-title">World Building Report</div>
            <div class="report-subtitle">Project: ${projectId}</div>
            <div class="report-subtitle">Generated on ${new Date().toLocaleDateString()}</div>
          </div>
          
          ${worldElements.locations && worldElements.locations.length > 0 ? `
            <h2>Locations (${worldElements.locations.length})</h2>
            ${worldElements.locations.map(location => `
              <div class="item">
                <h4>${location.name} 
                  ${location.importance ? `
                    <span class="importance-${location.importance}">(${location.importance.charAt(0).toUpperCase() + location.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${location.description || ''}</p>
                ${location.geography ? `<p><span class="field-label">Geography:</span> ${location.geography}</p>` : ''}
                ${location.climate ? `<p><span class="field-label">Climate:</span> ${location.climate}</p>` : ''}
                ${location.notableFeatures ? `<p><span class="field-label">Notable Features:</span> ${location.notableFeatures}</p>` : ''}
                ${location.inhabitants ? `<p><span class="field-label">Inhabitants:</span> ${location.inhabitants}</p>` : ''}
                ${location.details ? `<p class="details">${location.details}</p>` : ''}
                ${location.tags && location.tags.length > 0 ? `
                  <div class="tags-container">
                    ${location.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.cultures && worldElements.cultures.length > 0 ? `
            <h2>Cultures (${worldElements.cultures.length})</h2>
            ${worldElements.cultures.map(culture => `
              <div class="item">
                <h4>${culture.name} 
                  ${culture.importance ? `
                    <span class="importance-${culture.importance}">(${culture.importance.charAt(0).toUpperCase() + culture.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${culture.description || ''}</p>
                ${culture.traditions ? `<p><span class="field-label">Traditions:</span> ${culture.traditions}</p>` : ''}
                ${culture.values ? `<p><span class="field-label">Values:</span> ${culture.values}</p>` : ''}
                ${culture.socialStructure ? `<p><span class="field-label">Social Structure:</span> ${culture.socialStructure}</p>` : ''}
                ${culture.languages ? `<p><span class="field-label">Languages:</span> ${culture.languages}</p>` : ''}
                ${culture.details ? `<p class="details">${culture.details}</p>` : ''}
                ${culture.tags && culture.tags.length > 0 ? `
                  <div class="tags-container">
                    ${culture.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.systems && worldElements.systems.length > 0 ? `
            <h2>Rules & Systems (${worldElements.systems.length})</h2>
            ${worldElements.systems.map(system => `
              <div class="item">
                <h4>${system.name} 
                  ${system.importance ? `
                    <span class="importance-${system.importance}">(${system.importance.charAt(0).toUpperCase() + system.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${system.description || ''}</p>
                ${system.rules ? `<p><span class="field-label">Rules:</span> ${system.rules}</p>` : ''}
                ${system.mechanics ? `<p><span class="field-label">Mechanics:</span> ${system.mechanics}</p>` : ''}
                ${system.purpose ? `<p><span class="field-label">Purpose:</span> ${system.purpose}</p>` : ''}
                ${system.limitations ? `<p><span class="field-label">Limitations:</span> ${system.limitations}</p>` : ''}
                ${system.details ? `<p class="details">${system.details}</p>` : ''}
                ${system.tags && system.tags.length > 0 ? `
                  <div class="tags-container">
                    ${system.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.factions && worldElements.factions.length > 0 ? `
            <h2>Factions (${worldElements.factions.length})</h2>
            ${worldElements.factions.map(faction => `
              <div class="item">
                <h4>${faction.name} 
                  ${faction.importance ? `
                    <span class="importance-${faction.importance}">(${faction.importance.charAt(0).toUpperCase() + faction.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${faction.description || ''}</p>
                ${faction.goals ? `<p><span class="field-label">Goals:</span> ${faction.goals}</p>` : ''}
                ${faction.leadership ? `<p><span class="field-label">Leadership:</span> ${faction.leadership}</p>` : ''}
                ${faction.resources ? `<p><span class="field-label">Resources:</span> ${faction.resources}</p>` : ''}
                ${faction.allies ? `<p><span class="field-label">Allies:</span> ${faction.allies}</p>` : ''}
                ${faction.enemies ? `<p><span class="field-label">Enemies:</span> ${faction.enemies}</p>` : ''}
                ${faction.details ? `<p class="details">${faction.details}</p>` : ''}
                ${faction.tags && faction.tags.length > 0 ? `
                  <div class="tags-container">
                    ${faction.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.magic && worldElements.magic.length > 0 ? `
            <h2>Magic & Technology (${worldElements.magic.length})</h2>
            ${worldElements.magic.map(magic => `
              <div class="item">
                <h4>${magic.name} 
                  ${magic.importance ? `
                    <span class="importance-${magic.importance}">(${magic.importance.charAt(0).toUpperCase() + magic.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${magic.description || ''}</p>
                ${magic.rules ? `<p><span class="field-label">Rules:</span> ${magic.rules}</p>` : ''}
                ${magic.limitations ? `<p><span class="field-label">Limitations:</span> ${magic.limitations}</p>` : ''}
                ${magic.practitioners ? `<p><span class="field-label">Practitioners:</span> ${magic.practitioners}</p>` : ''}
                ${magic.history ? `<p><span class="field-label">History:</span> ${magic.history}</p>` : ''}
                ${magic.details ? `<p class="details">${magic.details}</p>` : ''}
                ${magic.tags && magic.tags.length > 0 ? `
                  <div class="tags-container">
                    ${magic.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.lore && worldElements.lore.length > 0 ? `
            <h2>Lore (${worldElements.lore.length})</h2>
            ${worldElements.lore.map(lore => `
              <div class="item">
                <h4>${lore.name} 
                  ${lore.importance ? `
                    <span class="importance-${lore.importance}">(${lore.importance.charAt(0).toUpperCase() + lore.importance.slice(1)})</span>
                  ` : ''}
                </h4>
                <p class="description">${lore.description || ''}</p>
                ${lore.origins ? `<p><span class="field-label">Origins:</span> ${lore.origins}</p>` : ''}
                ${lore.significance ? `<p><span class="field-label">Significance:</span> ${lore.significance}</p>` : ''}
                ${lore.believers ? `<p><span class="field-label">Believers:</span> ${lore.believers}</p>` : ''}
                ${lore.details ? `<p class="details">${lore.details}</p>` : ''}
                ${lore.tags && lore.tags.length > 0 ? `
                  <div class="tags-container">
                    ${lore.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${worldElements.timeline && worldElements.timeline.events && worldElements.timeline.events.length > 0 ? `
            <h2>Timeline (${worldElements.timeline.events.length} events)</h2>
            ${worldElements.timeline.eras && worldElements.timeline.eras.length > 0 ? `
              <p><span class="field-label">Eras:</span> ${worldElements.timeline.eras.map(era => era.name).join(', ')}</p>
            ` : ''}
            ${worldElements.timeline.events.map(event => `
              <div class="timeline-event">
                <h4>${event.title}</h4>
                <p class="timeline-date">${event.date || 'No date'}</p>
                ${event.era ? `<p class="timeline-era">Era: ${event.era}</p>` : ''}
                <p>${event.description || ''}</p>
                ${event.importance ? `
                  <p><span class="field-label">Importance:</span> <span class="importance-${event.importance}">${event.importance.charAt(0).toUpperCase() + event.importance.slice(1)}</span></p>
                ` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          <div class="report-footer">
            <p>Generated on ${new Date().toLocaleDateString()} for project ${projectId}</p>
            <p>SCAD Project Management System</p>
          </div>
        </body>
      </html>
    `);
    
    // Trigger the print dialog
    iframe.contentWindow.print();
    
    // Remove the iframe after printing
    iframe.remove();
  };
  
  // Function to directly download the PDF
  const handleDownloadPDF = async () => {
    const blob = await pdf(<WorldBuildingPDF worldElements={worldElements} projectId={projectId} />).toBlob();
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `world-building-${projectId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleDownload = () => {
    const content = JSON.stringify(worldElements, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `world-building-${projectId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Box sx={{ my: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            World Building Report
          </Typography>
          <Box>
            <Tooltip title="Print this report">
              <Button 
                variant="outlined" 
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ mr: 2, borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
              >
                Print Report
              </Button>
            </Tooltip>
            <Tooltip title="Download as PDF">
              <Button 
                variant="contained" 
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
              >
                Download PDF
              </Button>
            </Tooltip>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        <Box ref={printRef}>
          {/* Locations */}
          {worldElements.locations && worldElements.locations.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0d47a1' }}>
                Locations ({worldElements.locations.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.locations.map(location => (
                  <Grid item xs={12} sm={6} key={location.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {location.name}
                        {location.importance && (
                          <Chip 
                            size="small" 
                            label={location.importance.charAt(0).toUpperCase() + location.importance.slice(1)} 
                            color={
                              location.importance === 'high' ? 'error' : 
                              location.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {location.description || ''}
                      </Typography>
                      {location.geography && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Geography:</strong> {location.geography}
                        </Typography>
                      )}
                      {location.climate && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Climate:</strong> {location.climate}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Cultures */}
          {worldElements.cultures && worldElements.cultures.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4a148c' }}>
                Cultures ({worldElements.cultures.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.cultures.map(culture => (
                  <Grid item xs={12} sm={6} key={culture.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {culture.name}
                        {culture.importance && (
                          <Chip 
                            size="small" 
                            label={culture.importance.charAt(0).toUpperCase() + culture.importance.slice(1)} 
                            color={
                              culture.importance === 'high' ? 'error' : 
                              culture.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {culture.description || ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Systems */}
          {worldElements.systems && worldElements.systems.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1b5e20' }}>
                Rules & Systems ({worldElements.systems.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.systems.map(system => (
                  <Grid item xs={12} sm={6} key={system.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {system.name}
                        {system.importance && (
                          <Chip 
                            size="small" 
                            label={system.importance.charAt(0).toUpperCase() + system.importance.slice(1)} 
                            color={
                              system.importance === 'high' ? 'error' : 
                              system.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {system.description || ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Factions */}
          {worldElements.factions && worldElements.factions.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#b71c1c' }}>
                Factions ({worldElements.factions.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.factions.map(faction => (
                  <Grid item xs={12} sm={6} key={faction.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {faction.name}
                        {faction.importance && (
                          <Chip 
                            size="small" 
                            label={faction.importance.charAt(0).toUpperCase() + faction.importance.slice(1)} 
                            color={
                              faction.importance === 'high' ? 'error' : 
                              faction.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {faction.description || ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Magic/Tech */}
          {worldElements.magic && worldElements.magic.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4a148c' }}>
                Magic/Tech Systems ({worldElements.magic.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.magic.map(magic => (
                  <Grid item xs={12} sm={6} key={magic.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {magic.name}
                        {magic.importance && (
                          <Chip 
                            size="small" 
                            label={magic.importance.charAt(0).toUpperCase() + magic.importance.slice(1)} 
                            color={
                              magic.importance === 'high' ? 'error' : 
                              magic.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {magic.description || ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Lore */}
          {worldElements.lore && worldElements.lore.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#f57f17' }}>
                Lore ({worldElements.lore.length})
              </Typography>
              <Grid container spacing={2}>
                {worldElements.lore.map(lore => (
                  <Grid item xs={12} sm={6} key={lore.id}>
                    <Paper sx={{ p: 2, height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {lore.name}
                        {lore.importance && (
                          <Chip 
                            size="small" 
                            label={lore.importance.charAt(0).toUpperCase() + lore.importance.slice(1)} 
                            color={
                              lore.importance === 'high' ? 'error' : 
                              lore.importance === 'medium' ? 'primary' : 
                              'default'
                            }
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: 'text.secondary' }}>
                        {lore.description || ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Timeline */}
          {worldElements.timeline && worldElements.timeline.events && worldElements.timeline.events.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Timeline ({worldElements.timeline.events.length} events)
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ 
                position: 'relative', 
                pl: 4, 
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '16px',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bgcolor: 'primary.light',
                  zIndex: 0
                }
              }}>
                {worldElements.timeline.events
                  .sort((a, b) => {
                    // Sort by year first
                    if (a.year !== b.year) return a.year - b.year;
                    // If years are the same, sort by month
                    if (a.month !== b.month) return a.month - b.month;
                    // If months are the same, sort by day
                    return a.day - b.day;
                  })
                  .map((event, index) => (
                    <Box 
                      key={event.id || index}
                      sx={{ 
                        position: 'relative', 
                        mb: 4,
                        '&:last-child': {
                          mb: 0
                        }
                      }}
                    >
                      <Box sx={{ 
                        position: 'absolute',
                        left: '-24px',
                        top: '0px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        bgcolor: event.importance === 'high' ? 'error.main' : 
                                event.importance === 'medium' ? 'primary.main' : 'grey.500',
                        border: '2px solid white',
                        zIndex: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                      
                      <Card sx={{ 
                        borderRadius: 2, 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                        }
                      }}>
                        <CardHeader
                          title={
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, mr: 2 }}>
                                {event.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: 'text.secondary',
                                  bgcolor: 'grey.100',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                              >
                                <EventIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                Year {event.year}
                                {event.month && `, Month ${event.month}`}
                                {event.day && `, Day ${event.day}`}
                              </Typography>
                              {event.importance && (
                                <Chip 
                                  size="small" 
                                  label={event.importance.charAt(0).toUpperCase() + event.importance.slice(1)} 
                                  color={
                                    event.importance === 'high' ? 'error' : 
                                    event.importance === 'medium' ? 'primary' : 'default'
                                  }
                                  sx={{ ml: 1, fontWeight: 500 }}
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            pb: 0,
                            '& .MuiCardHeader-content': {
                              overflow: 'hidden'
                            }
                          }}
                        />
                        <CardContent>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {event.description}
                          </Typography>
                          
                          {event.consequences && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                Consequences:
                              </Typography>
                              <Typography variant="body2">
                                {event.consequences}
                              </Typography>
                            </Box>
                          )}
                          
                          {event.details && (
                            <Box sx={{ mt: 2 }}>
                              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body2">
                                  {event.details}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                          
                          {event.tags && event.tags.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {event.tags.map(tag => (
                                <Chip 
                                  key={tag} 
                                  label={tag} 
                                  size="small" 
                                  sx={{ 
                                    bgcolor: 'grey.100',
                                    borderRadius: 2,
                                    fontSize: '0.75rem'
                                  }} 
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default WorldBuildingReport;
